import { ReactNode, useState } from 'react'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import {
  IconButton,
  IconButtonProps,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material'
import EditableNameField from './EditableNameField'

type CommonProps = {
  groupName: string
  name: string
  showTooltip?: boolean
  typographyProps?: TypographyProps & { component?: React.ElementType }
}

type LabelProps = CommonProps & {
  isEditing: boolean
  handleOnBlur: (name: string) => void
}

type EditableNameWithActionsProps = CommonProps & {
  canBeEdited: boolean
  onDelete: () => void
  onUpdate: (name: string) => void
  deleteDisabledMessage?: string
  iconsSize?: IconButtonProps['size']
  fixedActions?: ReactNode
  hoverActions?: ReactNode
  onHoverChange?: (isHovering: boolean) => void
}

const Label = ({
  groupName,
  isEditing,
  name,
  showTooltip,
  handleOnBlur,
  typographyProps,
}: LabelProps) => {
  return isEditing ? (
    <EditableNameField name={name} handleOnBlur={handleOnBlur} />
  ) : (
    <Tooltip
      title={
        showTooltip
          ? `This is a default ${groupName}. It can't be edited or deleted.`
          : ''
      }
      enterDelay={1000}
    >
      <Typography {...typographyProps}>{name}</Typography>
    </Tooltip>
  )
}

export default function EditableNameWithActions({
  canBeEdited,
  onDelete,
  onUpdate,
  deleteDisabledMessage,
  iconsSize,
  fixedActions,
  hoverActions,
  onHoverChange,
  ...rest
}: EditableNameWithActionsProps) {
  const [showActionButtons, setShowActionButtons] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  function handleOnEdit(internalName: string) {
    setIsEditing(false)
    onUpdate(internalName)
  }

  return (
    <Stack
      alignItems="center"
      direction="row"
      onMouseEnter={() => {
        setShowActionButtons(true)
        onHoverChange?.(true)
      }}
      onMouseLeave={() => {
        setShowActionButtons(false)
        onHoverChange?.(false)
      }}
      spacing={1}
      sx={{
        maxWidth: '100%',
      }}
    >
      <Label
        isEditing={isEditing}
        handleOnBlur={handleOnEdit}
        typographyProps={{
          ...rest.typographyProps,
          sx: { wordBreak: 'break-all', fontSize: 200 },
        }}
        {...rest}
      />
      <Stack direction="row" alignItems="center">
        {!isEditing && fixedActions}
        {showActionButtons && !isEditing && hoverActions}
        {canBeEdited && showActionButtons && !isEditing && (
          <>
            <IconButton
              onClick={(e) => {
                e.preventDefault()
                setIsEditing(true)
              }}
              size={iconsSize}
            >
              <Edit color="primary" fontSize="inherit" />
            </IconButton>

            <Tooltip title={deleteDisabledMessage || ''} enterDelay={1000}>
              <span>
                <IconButton
                  disabled={!!deleteDisabledMessage}
                  onClick={(e) => {
                    e.preventDefault()
                    onDelete()
                  }}
                  size={iconsSize}
                >
                  <Delete
                    color={deleteDisabledMessage ? 'disabled' : 'error'}
                    fontSize="inherit"
                  />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>
    </Stack>
  )
}
